import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FiChevronDown, FiChevronRight, FiMoreVertical, FiShare2, FiEye, FiEyeOff, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search-sm.svg';
import { RootState, AppDispatch } from '../../../store';
import { ConversationListItem, markConversationAsRead, markConversationAsUnread, renameConversation } from '../../../store/aiChatSlice';
import { setModalState, modalViewsConst } from '../../../store/modalSlice';
import { Skeleton, SkeletonGroup } from "../../Skeleton";

// Recent = conversations within this many days; the rest fall into Past
const RECENT_DAYS = 7;

// Approx options-menu height (px); if less room than this below the kebab, flip it upward
const MENU_ESTIMATED_HEIGHT = 130;

// Whole days between the given date and today (0 = today, 1 = yesterday)
const getDayDiff = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const startOfGivenDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((startOfToday.getTime() - startOfGivenDate.getTime()) / (1000 * 60 * 60 * 24));
};

interface ChatHistoryProps {
  currentConversationId?: string;
  onConversationSelect: (conversation: ConversationListItem) => void;
  onNewConversation: () => void;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
  currentConversationId,
  onConversationSelect,
  onNewConversation,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const conversations = useSelector((state: RootState) => state.aiChat.conversations);
  const conversationsLoading = useSelector((state: RootState) => state.aiChat.conversationsLoading);
  const lastResult = useSelector((state: RootState) => state.modalState.lastResult);
  const isLoading = conversationsLoading === 'pending';

  const handledResultRef = useRef<any>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [isRecentExpanded, setIsRecentExpanded] = useState(true);
  const [isPastExpanded, setIsPastExpanded] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  // Whether the open menu flips upward because the row sits near the scroll container's bottom
  const [menuDropUp, setMenuDropUp] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Set on Escape so the resulting unmount blur doesn't save the cancelled draft
  const cancelRenameRef = useRef(false);

  // Close the open kebab menu on any outside click
  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const filteredConversations = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (query === '') return conversations;
    return conversations.filter((c) => (c.title ?? '').toLowerCase().includes(query));
  }, [conversations, searchTerm]);

  // Split the (already most-recent-first) list: Recent = within RECENT_DAYS, rest → Past
  const { recentConversations, pastConversations } = useMemo(() => {
    const recent: ConversationListItem[] = [];
    const past: ConversationListItem[] = [];
    filteredConversations.forEach((c) => {
      const diff = getDayDiff(c.lastMessageAt);
      if (!Number.isNaN(diff) && diff <= RECENT_DAYS) {
        recent.push(c);
      } else {
        past.push(c);
      }
    });
    return { recentConversations: recent, pastConversations: past };
  }, [filteredConversations]);

  useEffect(() => {
    if (
      lastResult?.confirmed === true &&
      lastResult?.itemId &&
      lastResult?.itemType === 'conversation' &&
      lastResult !== handledResultRef.current
    ) {
      handledResultRef.current = lastResult;
      if (lastResult.itemId === currentConversationId) {
        onNewConversation();
      }
    }
  }, [lastResult, currentConversationId, onNewConversation]);

  const formatDate = (dateString: string) => {
    const diffDays = getDayDiff(dateString);
    if (Number.isNaN(diffDays)) return '';
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays < 28) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }
    if (diffDays < 365) {
      const months = Math.max(1, Math.floor(diffDays / 30));
      return `${months} month${months > 1 ? 's' : ''} ago`;
    }
    const years = Math.max(1, Math.floor(diffDays / 365));
    return `${years} year${years > 1 ? 's' : ''} ago`;
  };

  const handleDeleteClick = (conversation: ConversationListItem, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.generalDelete,
      properties: {
        __modalSize: 'sm',
        title: 'Delete Conversation',
        itemName: conversation.title,
        itemId: conversation.id,
        itemType: 'conversation',
      },
    }));
  };

  // Enter inline-edit mode for a conversation title
  const startRename = (conversation: ConversationListItem) => {
    // Reset in case a previous rename was cancelled via Escape — the input's unmount doesn't reliably fire onBlur, so the flag can otherwise linger and swallow this rename's commit
    cancelRenameRef.current = false;
    setOpenMenuId(null);
    setEditingId(conversation.id);
    setDraftTitle(conversation.title);
  };

  // Persist the rename only when the trimmed title actually changed; always exit edit mode
  const commitRename = (conversation: ConversationListItem) => {
    if (cancelRenameRef.current) {
      cancelRenameRef.current = false;
      return;
    }
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== conversation.title) {
      dispatch(renameConversation({ conversationId: conversation.id, title: trimmed, previousTitle: conversation.title }));
    }
    setEditingId(null);
  };

  // Open the share modal (link-only mode) for the whole conversation — matches AiChatHeader
  const handleShareClick = (conversation: ConversationListItem) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.shareEmbed,
      properties: { __modalSize: 'sm', conversationId: conversation.id },
    }));
  };

  // Open the options menu, flipping it upward when the kebab is too close to the scroll container's bottom
  const openMenu = (conversationId: string, buttonEl: HTMLElement) => {
    const container = scrollContainerRef.current;
    const spaceBelow = container
      ? container.getBoundingClientRect().bottom - buttonEl.getBoundingClientRect().bottom
      : Infinity;
    setMenuDropUp(spaceBelow < MENU_ESTIMATED_HEIGHT);
    setOpenMenuId(conversationId);
  };

  const renderConversationRow = (conversation: ConversationListItem) => {
    const isMenuOpen = openMenuId === conversation.id;
    const isEditing = editingId === conversation.id;
    return (
    <div
      key={conversation.id}
      onClick={() => !isEditing && onConversationSelect(conversation)}
      className={`group relative cursor-pointer rounded-eai-card px-2.5 py-2 transition-colors hover:bg-eai-surface-sunken active:bg-eai-surface-hover
        ${currentConversationId === conversation.id
          ? 'bg-eai-checklist-lt before:absolute before:inset-y-2 before:left-0 before:w-[2px] before:rounded-full before:bg-eai-cta-primary'
          : ''}
      `}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1.5">
          {isEditing ? (
            <input
              autoFocus
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={() => commitRename(conversation)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitRename(conversation); }
                if (e.key === 'Escape') { e.preventDefault(); cancelRenameRef.current = true; setEditingId(null); }
              }}
              className="eai-input min-w-0 flex-1 px-1.5 py-0.5 text-eai-body-sm font-medium"
            />
          ) : (
            <h3 className="eai-card-title flex-1 truncate font-medium">
              {conversation.title}
            </h3>
          )}
          {/* Alert/kebab slot collapses until hover on desktop; delete stays pinned right and always visible */}
          <div className="flex-shrink-0 flex items-center gap-1">
            {/* Mobile-only alert dot */}
            {conversation.unreadCount > 0 && (
              <span className="md:hidden flex items-center justify-center w-6 h-6" title={`${conversation.unreadCount} unread`}>
                <span className="w-2 h-2 rounded-full bg-eai-cta-primary" />
              </span>
            )}
            {/* Swap slot: desktop shows the dot at rest and the kebab on hover in the same spot */}
            <div
              ref={isMenuOpen ? menuRef : undefined}
              className={`relative flex items-center justify-center h-6 w-6 transition-all ${conversation.unreadCount > 0 || isMenuOpen ? '' : 'md:w-0 md:group-hover:w-6 md:group-focus-within:w-6'}`}
            >
              {conversation.unreadCount > 0 && (
                <span
                  className={`hidden md:flex absolute inset-0 items-center justify-center pointer-events-none transition-opacity group-hover:opacity-0 ${isMenuOpen ? 'opacity-0' : ''}`}
                  title={`${conversation.unreadCount} unread`}
                >
                  <span className="w-2 h-2 rounded-full bg-eai-cta-primary" />
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMenuOpen) setOpenMenuId(null);
                  else openMenu(conversation.id, e.currentTarget);
                }}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                className={`flex items-center justify-center w-full h-full rounded-full hover:bg-eai-surface-active active:bg-eai-surface-active transition-opacity md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto md:group-focus-within:opacity-100 md:group-focus-within:pointer-events-auto ${isMenuOpen ? 'md:opacity-100 md:pointer-events-auto bg-eai-surface-active' : ''}`}
                title="Conversation options"
              >
                <FiMoreVertical size={14} />
              </button>
              {isMenuOpen && (
                <div
                  role="menu"
                  className={`absolute right-0 ${menuDropUp ? 'bottom-full mb-1' : 'top-full mt-1'} eai-card z-10 min-w-[160px] p-1`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* One entry only — flips to the action that makes sense for the chat's current unread state */}
                  {conversation.unreadCount > 0 ? (
                    <button
                      role="menuitem"
                      onClick={() => { setOpenMenuId(null); dispatch(markConversationAsRead({ conversationId: conversation.id, previousUnreadCount: conversation.unreadCount })); }}
                      className="eai-menu-item flex w-full items-center gap-2 whitespace-nowrap text-left"
                    >
                      <FiEye className="h-4 w-4 flex-none" />
                      Mark as read
                    </button>
                  ) : (
                    <button
                      role="menuitem"
                      onClick={() => { setOpenMenuId(null); dispatch(markConversationAsUnread({ conversationId: conversation.id, previousUnreadCount: conversation.unreadCount })); }}
                      className="eai-menu-item flex w-full items-center gap-2 whitespace-nowrap text-left"
                    >
                      <FiEyeOff className="h-4 w-4 flex-none" />
                      Mark as unread
                    </button>
                  )}
                  <button
                    role="menuitem"
                    onClick={() => startRename(conversation)}
                    className="eai-menu-item flex w-full items-center gap-2 whitespace-nowrap text-left"
                  >
                    <FiEdit2 className="h-4 w-4 flex-none" />
                    Rename
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setOpenMenuId(null); handleShareClick(conversation); }}
                    className="eai-menu-item flex w-full items-center gap-2 whitespace-nowrap text-left"
                  >
                    <FiShare2 className="h-4 w-4 flex-none" />
                    Share
                  </button>
                </div>
              )}
            </div>
            {/* Same round pill and red fill as the card delete on Home, minus its resting frame — a list row is too dense to carry a bordered chip on every line */}
            <button
              onClick={(e) => handleDeleteClick(conversation, e)}
              className="eai-btn-icon eai-btn-icon-danger flex-shrink-0"
              title="Delete conversation"
              aria-label="Delete conversation"
            >
              <FiTrash2 size={13} />
            </button>
          </div>
        </div>
        {/* Date leads; the count is pushed right so it sits under the delete button */}
        <p className="font-inter flex items-center justify-between gap-2 text-eai-caption text-eai-ink-faint">
          <span className="truncate">{formatDate(conversation.lastMessageAt)}</span>
          <span className="flex-none">
            {conversation.messageCount} {conversation.messageCount === 1 ? 'message' : 'messages'}
          </span>
        </p>
      </div>
    </div>
    );
  };

  const renderSection = (
    label: string,
    items: ConversationListItem[],
    isExpanded: boolean,
    onToggle: () => void,
  ) => {
    if (items.length === 0) return null;
    return (
      <div>
        <button onClick={onToggle} className="w-full flex items-center gap-2 px-2 py-1.5 group">
          <span className="text-eai-ink-faint">
            {isExpanded ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
          </span>
          <span className="eai-eyebrow transition-colors group-hover:text-eai-ink">{label}</span>
          <span className="eai-count-pill">{items.length}</span>
          <span className="ml-1 h-px flex-1 bg-eai-surface-active" />
        </button>
        {isExpanded && <div className="mt-1 space-y-1">{items.map(renderConversationRow)}</div>}
      </div>
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-none px-3 pb-2 pt-3">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-eai-ink-faint" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search chats"
            aria-label="Search chats"
            className="eai-input pl-9"
          />
        </div>
      </div>
      <div ref={scrollContainerRef} className="flex-1 min-h-0 space-y-1 overflow-y-auto px-2.5 pb-3">
      {isLoading ? (
        <SkeletonGroup count={6} label="Loading conversations" className="space-y-1">
          <div className="px-2.5 py-2">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="mt-1.5 h-2.5 w-1/3" />
          </div>
        </SkeletonGroup>
      ) : conversations.length === 0 ? (
        <div className="text-center py-8">
          <p className="eai-description-sm">No conversations yet</p>
          <p className="font-inter mt-1 text-eai-body-xs text-eai-ink-faint">Start a new chat to begin</p>
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className="text-center py-8">
          <p className="eai-description-sm">No chats match</p>
          <p className="font-inter mt-1 text-eai-body-xs text-eai-ink-faint">Try a different search</p>
        </div>
      ) : searchTerm.trim() ? (
        // While searching, show a flat list so matches can't hide in a collapsed section
        <div className="space-y-1">{filteredConversations.map(renderConversationRow)}</div>
      ) : (
        <>
          {renderSection('Recent', recentConversations, isRecentExpanded, () => setIsRecentExpanded((v) => !v))}
          {renderSection('Past', pastConversations, isPastExpanded, () => setIsPastExpanded((v) => !v))}
        </>
      )}
      </div>
    </div>
  );
};

export default ChatHistory;
